/**
 * Palettes — the "palette" trait.
 *
 * A palette is eleven authored colours, not a generated gradient. Procedural HSL
 * wheels are the fastest way to make a generative collection look cheap: every
 * token lands somewhere plausible and nowhere memorable. These are hand-mixed,
 * each with a stated intent, and the terrain ramp is interpolated from four stops
 * at render time so elevation reads as a continuous surface rather than eight
 * unrelated colours.
 *
 * Slots:
 *
 *   bg        canvas
 *   terrain   four stops, low ground to high ground, expanded to eight bands
 *   plumage   shadow / mid / light — back, wing and head
 *   breast    the bird's defining mark; deliberately the loudest colour
 *   accent    beak and legs
 *   spark     eye highlight, the single brightest pixel in the piece
 *   ink       outline; sits between the bird and the ground
 *
 * `light: true` marks palettes whose background is light. Those need inverted
 * glitch blending, so the flag is part of the data rather than inferred.
 *
 * Contrast is verified, not assumed: `tools/palettecheck.ts` computes WCAG
 * relative luminance for every slot pair that must stay distinguishable and fails
 * the build if a token could render as mush.
 */

export interface Palette {
  readonly name: string;
  readonly bg: number;
  /** Low ground to high ground. Expanded to `ELEVATION_LEVELS` bands at render. */
  readonly terrain: readonly [number, number, number, number];
  /** Shadow, mid, light. */
  readonly plumage: readonly [number, number, number];
  readonly breast: number;
  readonly accent: number;
  readonly spark: number;
  readonly ink: number;
  /** True when `bg` is a light colour. */
  readonly light: boolean;
  readonly weight: number;
}

export const PALETTES: readonly Palette[] = [
  {
    // The namesake. European robin: brown back, rust breast, damp woodland floor.
    name: "Loam",
    bg: 0x0d0b08,
    terrain: [0x2a2418, 0x4a3a22, 0x6f5a32, 0xa89060],
    plumage: [0x33241b, 0x58402e, 0x8a6349],
    breast: 0xe35f2c,
    accent: 0xe8b84b,
    spark: 0xffe9b0,
    ink: 0x060504,
    light: false,
    weight: 14,
  },
  {
    // Hard winter light. Almost no warm pigment anywhere except the eye.
    name: "Frost",
    bg: 0x0a1016,
    terrain: [0x131f2a, 0x24384a, 0x3e5d75, 0x8bb3ce],
    plumage: [0x22364a, 0x4a7290, 0x93bcd6],
    breast: 0xeaf6fc,
    accent: 0xb8d8e8,
    spark: 0xffffff,
    ink: 0x050a0f,
    light: false,
    weight: 11,
  },
  {
    // Volcanic. Terrain glows hotter as it rises, which inverts the usual reading.
    name: "Ember",
    bg: 0x100604,
    terrain: [0x260c06, 0x55190c, 0x8a2a10, 0xd05a26],
    plumage: [0x330f07, 0x74260e, 0xb85028],
    breast: 0xff8a3c,
    accent: 0xffc247,
    spark: 0xfff0c0,
    ink: 0x070302,
    light: false,
    weight: 8,
  },
  {
    // Oxidised copper. Cool greens with a single struck-brass accent.
    name: "Verdigris",
    bg: 0x071110,
    terrain: [0x0e1f1d, 0x1b3a34, 0x2d5c50, 0x56997f],
    plumage: [0x122c28, 0x2c5b50, 0x64a893],
    breast: 0xa8e0cc,
    accent: 0xc9a227,
    spark: 0xeaf7f0,
    ink: 0x040c0b,
    light: false,
    weight: 9,
  },
  {
    // Light ground. Reads as an ink drawing on aged paper, so the bird is *darker*
    // than the terrain here — the inverse of every dark palette. The breast has to
    // be a deep red rather than a bright one: on a near-white ground, brightening
    // it costs more contrast against `bg` than it gains against the plumage.
    name: "Bone",
    bg: 0xe8e3d8,
    terrain: [0xd8d1c2, 0xbdb3a0, 0x9a9080, 0x6b6252],
    plumage: [0x1c1a15, 0x302c22, 0x6e6656],
    breast: 0xb8431c,
    accent: 0x3f3a30,
    spark: 0xffffff,
    ink: 0x22201a,
    light: true,
    weight: 6,
  },
  {
    // The one palette allowed to be synthetic. Magenta breast, cyan beak.
    name: "Ultraviolet",
    bg: 0x0a0714,
    terrain: [0x150c28, 0x2e1a54, 0x4b2a80, 0x8055c8],
    plumage: [0x20143a, 0x4a2c80, 0x8a68c8],
    breast: 0xe85bd4,
    accent: 0x38e0d8,
    spark: 0xf2e6ff,
    ink: 0x05030c,
    light: false,
    weight: 5,
  },
  {
    // Sodium street lamp at 3am. Everything is navy except what the lamp touches.
    name: "Sodium",
    bg: 0x06080f,
    terrain: [0x0c1020, 0x1c2540, 0x303a5c, 0x626f9c],
    plumage: [0x161b2e, 0x38405e, 0x7a83a4],
    breast: 0xff9d2e,
    accent: 0xffc76b,
    spark: 0xfff3d0,
    ink: 0x030409,
    light: false,
    weight: 9,
  },
  {
    // Overgrowth. The brightest terrain of any palette.
    name: "Chlorophyll",
    bg: 0x060d07,
    terrain: [0x0c1c0e, 0x17361a, 0x245226, 0x458c42],
    plumage: [0x16391b, 0x327433, 0x5cb254],
    breast: 0xc4f038,
    accent: 0xe0f56b,
    spark: 0xf4ffd8,
    ink: 0x030702,
    light: false,
    weight: 8,
  },
  {
    // Iron oxide and ochre. Closest neighbour to Loam, drier and more orange.
    name: "Rust",
    bg: 0x0e0a08,
    terrain: [0x1f1611, 0x3f2c1e, 0x63452c, 0x99724a],
    plumage: [0x261a12, 0x4a3223, 0x7d5840],
    breast: 0xd24f22,
    accent: 0xd98c3a,
    spark: 0xf0d2a0,
    ink: 0x070504,
    light: false,
    weight: 10,
  },
  {
    // Deep water with a bioluminescent bird. Highest breast-to-ground contrast.
    name: "Abyss",
    bg: 0x04080f,
    terrain: [0x06101e, 0x10263f, 0x193d61, 0x2d6ea6],
    plumage: [0x0b1a2e, 0x1d4570, 0x4285be],
    breast: 0x2ee0c8,
    accent: 0x7ff0e0,
    spark: 0xe0fffa,
    ink: 0x020509,
    light: false,
    weight: 7,
  },
  {
    // Light ground, near-monochrome, one red mark. The most graphic palette, and
    // the one that proved the tint had to be split: at full strength on every slot
    // the red mark dropped to 1.37 against its own plumage.
    name: "Salt",
    bg: 0xf2f2ef,
    terrain: [0xe2e2dc, 0xc6c6be, 0xa4a49c, 0x767670],
    plumage: [0x1e1e1a, 0x2e2e2a, 0x74746e],
    breast: 0xc4241d,
    accent: 0x1a1a18,
    spark: 0xffffff,
    ink: 0x222220,
    light: true,
    weight: 4,
  },
  {
    // Fired clay and sand. Warm neutrals, low drama.
    name: "Terracotta",
    bg: 0x120c09,
    terrain: [0x281a12, 0x4e3423, 0x764e33, 0xb37a50],
    plumage: [0x32201a, 0x5e3c28, 0x996244],
    breast: 0xe57a4c,
    accent: 0xefc07a,
    spark: 0xfbe8cf,
    ink: 0x080504,
    light: false,
    weight: 10,
  },
  {
    // Ash. Fully desaturated except the eye, which is the only warm colour in the
    // frame. Measurement moved it twice: a pure red (#ff3b30) was no brighter than
    // the lightest feathers, so it read as a hole rather than a highlight. The head
    // was darkened and the eye warmed to coral until it is unambiguously the
    // brightest pixel on the bird, which is the whole point of this palette.
    name: "Cinder",
    bg: 0x0a0a0b,
    terrain: [0x141416, 0x2c2c30, 0x46464c, 0x74747c],
    plumage: [0x1e1e22, 0x4e4e58, 0x82828e],
    breast: 0xc0c0c8,
    accent: 0xd8d8e0,
    spark: 0xff8a72,
    ink: 0x050505,
    light: false,
    weight: 6,
  },
  {
    // Cold ground, warm bird, violet beak. The only palette with a split accent.
    name: "Aurora",
    bg: 0x050a10,
    terrain: [0x081820, 0x103038, 0x164a4a, 0x23806c],
    plumage: [0x0d2130, 0x1d5560, 0x38a89e],
    breast: 0x7cf0b0,
    accent: 0xb47cf0,
    spark: 0xeafff4,
    ink: 0x03070a,
    light: false,
    weight: 5,
  },
  {
    // Deliberately uneasy: bone pinks, no green anywhere.
    name: "Marrow",
    bg: 0x0f0a0c,
    terrain: [0x201316, 0x3e2226, 0x603439, 0x97595e],
    plumage: [0x271619, 0x543034, 0x8f5f64],
    breast: 0xe8b0b0,
    accent: 0xc8748a,
    spark: 0xfff0f0,
    ink: 0x080506,
    light: false,
    weight: 4,
  },
  {
    // Phosphor CRT. Breast and accent are the same colour on purpose — the bird
    // is one signal, not a creature with parts. Its terrain is the narrowest in the
    // collection by nature (one hue, low in the green channel only), so the stops
    // are pushed wider than they look like they need: the Blood tint compresses
    // dark green ramps harder than any other pairing in the matrix.
    name: "Signal",
    bg: 0x030603,
    terrain: [0x040e04, 0x0f2c12, 0x1a5220, 0x2a9636],
    plumage: [0x0e2a12, 0x1e6b28, 0x33b040],
    breast: 0x39ff6a,
    accent: 0x39ff6a,
    spark: 0xd8ffe0,
    ink: 0x020402,
    light: false,
    weight: 3,
  },
] as const;

export const PALETTE_WEIGHTS: readonly number[] = PALETTES.map((p) => p.weight);

/**
 * Per-token tint targets. A small seeded pull toward one of these shifts a
 * palette's whole mood without breaking the authored relationships between slots —
 * the alternative, rotating hue, drifts colours past each other and destroys the
 * contrast guarantees.
 *
 * `None` is first and is drawn with a strength of exactly zero, so an untinted token
 * is the authored palette and nothing else. It carries the heaviest weight because
 * the palettes were authored to stand on their own; the tint is a variation on them,
 * not a rescue for them.
 */
export const TINTS: readonly { readonly name: string; readonly rgb: number; readonly weight: number }[] = [
  { name: "None", rgb: 0x000000, weight: 22 },
  { name: "Gold", rgb: 0xffc247, weight: 12 },
  { name: "Rose", rgb: 0xff6a8a, weight: 10 },
  { name: "Cyan", rgb: 0x38e0d8, weight: 11 },
  { name: "Violet", rgb: 0x9a6cff, weight: 10 },
  { name: "Lime", rgb: 0xb8e830, weight: 9 },
  { name: "Ash", rgb: 0x8a8a92, weight: 12 },
  { name: "Blood", rgb: 0xc01f14, weight: 8 },
] as const;

/** Index of the untinted option. Its strength is forced to zero. */
export const TINT_NONE = 0;

export const TINT_WEIGHTS: readonly number[] = TINTS.map((t) => t.weight);

/** Structural guard invoked by tests. */
export function validatePalettes(): void {
  const names = new Set<string>();
  for (const p of PALETTES) {
    if (names.has(p.name)) throw new Error(`duplicate palette: ${p.name}`);
    names.add(p.name);
    if (p.weight <= 0) throw new Error(`${p.name}: weight must be positive`);
    if (p.terrain.length !== 4) throw new Error(`${p.name}: needs exactly 4 terrain stops`);
    if (p.plumage.length !== 3) throw new Error(`${p.name}: needs exactly 3 plumage stops`);
    const all = [p.bg, ...p.terrain, ...p.plumage, p.breast, p.accent, p.spark, p.ink];
    for (const c of all) {
      if (!Number.isInteger(c) || c < 0 || c > 0xffffff) {
        throw new Error(`${p.name}: colour ${c} is not a 24-bit RGB value`);
      }
    }
  }
  for (const t of TINTS) {
    if (!Number.isInteger(t.rgb) || t.rgb < 0 || t.rgb > 0xffffff) {
      throw new Error(`tint ${t.name}: not a 24-bit RGB value`);
    }
    if (t.weight <= 0) throw new Error(`tint ${t.name}: weight must be positive`);
  }
  if ((TINTS[TINT_NONE] as { rgb: number }).rgb !== 0) {
    throw new Error("TINT_NONE must be the black entry, which the zero strength relies on");
  }
}
